import { useState } from "react";

const SOURCE_FIELDS = [
  { name: "System Name",           desc: "Your ERP system identifier (e.g. SAP-NA, Oracle)" },
  { name: "Vendor / Customer #",   desc: "Original vendor or customer number from your system" },
  { name: "Vendor Name",           desc: "Name exactly as submitted in your upload" },
  { name: "Address Line 1–4",      desc: "Address lines as submitted" },
  { name: "City / State / ZIP / Country", desc: "Location as submitted" },
  { name: "Phone",                 desc: "Phone as submitted" },
  { name: "Email",                 desc: "Email as submitted" },
  { name: "Tax ID",                desc: "Tax ID / EIN as submitted" },
  { name: "Website / Domain",      desc: "Domain as submitted (if provided)" },
];

const AI_FIELDS = [
  { name: "AI Vendor Name",        desc: "Company name cleaned and standardized — corrects abbreviations, casing, and legal suffixes" },
  { name: "AI Address Line 1–4",   desc: "USPS-style standardized address — corrects abbreviations, directionals, and street types" },
  { name: "AI PO Box",             desc: "PO Box extracted from address lines if present" },
  { name: "AI City / State / Postal Code / Country", desc: "Standardized location fields" },
  { name: "AI Phone Formatted",    desc: "Phone in standard US format, e.g. (555) 123-4567" },
  { name: "AI Phone E.164",        desc: "International format for API/system use, e.g. +15551234567" },
  { name: "AI Phone Digits Only",  desc: "Digits only — useful for direct ERP import" },
  { name: "AI Email",              desc: "Cleaned and lowercased email address" },
  { name: "AI Tax ID",             desc: "Tax ID / EIN with formatting normalized" },
  { name: "AI Domain",             desc: "Company website domain, verified where possible (e.g. acme.com)" },
  { name: "AI Domain Confidence %", desc: "0–100 score for how certain the AI is about the domain" },
  { name: "AI Domain Source",      desc: "How the domain was found — LLM1, LLM2, LLM Search, or Input Domain (Verified)" },
  { name: "AI NAICS Code",         desc: "6-digit NAICS industry classification code" },
  { name: "AI NAICS Title",        desc: "Industry name corresponding to the NAICS code" },
  { name: "AI NAICS Confidence %", desc: "Confidence score for the NAICS assignment" },
  { name: "AI Alt NAICS 1 & 2",    desc: "Alternative industry classifications considered — useful for borderline cases" },
  { name: "AI Business Description", desc: "1–2 sentence summary of what the company does" },
  { name: "AI Key Offerings",      desc: "Main products or services, semicolon-separated" },
  { name: "AI Industries Served",  desc: "Customer segments or industries the company sells into" },
  { name: "AI Profile Confidence %", desc: "Overall confidence across domain, NAICS, and profile data combined" },
  { name: "AI Is Person",          desc: "Yes if the record is an individual person rather than a company" },
  { name: "AI Is Intercompany",    desc: "Yes if the record is an internal entity (subsidiary, division, etc.)" },
  { name: "AI Enrichment Status",  desc: "Enriched or Error" },
  { name: "AI Error Message",      desc: "Details if enrichment failed for this record" },
];

const DUP_FIELDS = [
  { name: "Dup Domain (Same System)",  desc: "Shared domain code (DOM-xxx) for vendors with the same website within your ERP — likely the same company entered twice" },
  { name: "Dup Domain (Cross System)", desc: "Shared domain code (DOM-xxx) for vendors with the same website across different source systems — same real-world company under different numbers" },
  { name: "Dup Address",              desc: "Shared address code (ADR-xxx) for vendors with the same address fingerprint — may indicate duplicates or related entities" },
];

function FieldRow({ name, desc }) {
  return (
    <div style={styles.fieldRow}>
      <div style={styles.fieldName}>{name}</div>
      <div style={styles.fieldDesc}>{desc}</div>
    </div>
  );
}

function Section({ title, fields }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={styles.sectionLabel}>{title}</div>
      {fields.map(f => <FieldRow key={f.name} {...f} />)}
    </div>
  );
}

export default function DownloadModal({ onClose, onDownload, downloading, vendorCount, addressCount, includeDups = false }) {
  const [hover, setHover] = useState(false);

  const countLine = [
    vendorCount != null && `${vendorCount.toLocaleString()} vendor${vendorCount !== 1 ? "s" : ""}`,
    addressCount != null && addressCount > vendorCount && `${addressCount.toLocaleString()} addresses`,
  ].filter(Boolean).join(", ");

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Download Enriched Data</h2>
            {countLine && <p style={styles.subtitle}>{countLine} · CSV format · UTF-8 with BOM</p>}
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Download button — right under heading, before field list */}
        <div style={styles.actionRow}>
          <button
            style={{ ...styles.downloadBtn, opacity: downloading ? 0.7 : 1, background: hover && !downloading ? "#d9631f" : "#F37428" }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={onDownload}
            disabled={downloading}
          >
            {downloading ? "Preparing download…" : "↓ Download CSV"}
          </button>
          <span style={styles.actionNote}>One row per address. AI fields are empty for records that could not be enriched.</span>
        </div>

        <div style={styles.divider} />

        {/* Scrollable field legend */}
        <div style={styles.fieldList}>
          <Section title="Source Data — your original import" fields={SOURCE_FIELDS} />
          <Section title="AI Enrichment — new data added by MasterFileAI" fields={AI_FIELDS} />
          {includeDups && <Section title="Duplicate Detection — run Analyze on the Master File page" fields={DUP_FIELDS} />}
        </div>

      </div>
    </div>
  );
}

const styles = {
  overlay:     { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  modal:       { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 640, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden" },
  header:      { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "24px 28px 0" },
  title:       { fontSize: 20, fontWeight: 700, color: "#111827", margin: 0, letterSpacing: "-0.3px" },
  subtitle:    { fontSize: 13, color: "#6b7280", margin: "4px 0 0" },
  closeBtn:    { background: "none", border: "none", fontSize: 16, color: "#9ca3af", cursor: "pointer", padding: "4px 6px", flexShrink: 0, marginLeft: 16, marginTop: -2 },
  actionRow:   { display: "flex", alignItems: "center", gap: 14, padding: "16px 28px" },
  downloadBtn: { background: "#F37428", color: "#fff", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", transition: "background 0.15s" },
  actionNote:  { fontSize: 12, color: "#9ca3af", lineHeight: 1.4 },
  divider:     { borderTop: "1px solid #f1f5f9", flexShrink: 0 },
  fieldList:   { overflowY: "auto", padding: "20px 28px 24px", flex: 1 },
  sectionLabel:{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10, marginTop: 4 },
  fieldRow:    { display: "grid", gridTemplateColumns: "220px 1fr", gap: 12, padding: "7px 0", borderBottom: "1px solid #f8fafc" },
  fieldName:   { fontSize: 13, fontWeight: 600, color: "#374151" },
  fieldDesc:   { fontSize: 13, color: "#6b7280", lineHeight: 1.4 },
};
